import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { format } from 'date-fns';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import slice from 'lodash/slice';
import find from 'lodash/find';
import uniqBy from 'lodash/uniqBy';
import debounce from 'lodash/debounce';
import remove from 'lodash/remove';

import useLocalStorage from '@hooks/useLocalStorage';
import config from '@configFile';

import { PageDeal } from '@api/database/models/pageDeal';
import { useGetPageDealSlugs } from '@hooks/useGetPageDeal';
import { useGetCustomBlogPosts } from '@hooks/useCustomBlogPosts';
import { useGetLatestDeals } from '@hooks/useDeals';
import trackUse from '@utils/trackUse';
import { logPostHogEvent } from '@utils/index';
import ScrollTop from '@components/ScrollTop';
import Email from '@pages/Email/Email';

import {
  DEALS_UPDATE_BLOG_PAGE_SEO,
  DealPostType,
  FeedObjectType,
  AppState,
  CustomBlogPostUser
} from '@types';
import Title from '@pages/Blog/components/Title';
import { useGetUserData } from '@hooks/useGetUserData';
import DealPostFeedCard from './components/DealPostFeedCard';
import PageDealFeedCard from './components/PageDealFeedCard';
import CustomBlogPostCard from './components/CustomBlogPostCard';
import TopDealsList from './components/TopDealsList';
import BlogContainer from './BlogContainer';
import SkeletonBlogCard from './SkeletonBlogCard';

import {
  postDataToFeedObj,
  pageDealsToFeedObjs,
  customBlogPostsToFeedObj,
  daysToFeed
} from './utils';

const loadingLimit = 2;
const emailSignUpPromptPosition = 2;

interface BlogProps extends ReturnType<typeof mapStateToProps> {
  renderInContainer?: boolean;
  shouldRenderSEO?: boolean;
  filterOutSlug?: string;
  staticContext?: { status: string };
}

const Blog = ({
  renderInContainer = true,
  shouldRenderSEO = true,
  filterOutSlug = '',
  blogPageSEO,
  staticContext,
  dealSummaryError
}: BlogProps) => {
  if (staticContext && dealSummaryError && renderInContainer) {
    console.log('404');
    staticContext.status = '404';
  }

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const dispatch = useDispatch();
  const [hasSignedUpForMailingLists] = useLocalStorage(
    config.HAS_SIGNED_UP_FOR_MAILING_LISTS,
    false
  );
  const { data: user } = useGetUserData();
  const { data: customBlogPostsData, isLoading: isLoadingCustomBlogPosts } =
    useGetCustomBlogPosts();
  const { data: deals, isLoading: isLoadingDeals } = useGetLatestDeals();
  const { data: pageDealSlugs, isLoading: isLoadingPageDealSlugs } =
    useGetPageDealSlugs();

  const [limit, setLimit] = useState<number | null>(1);
  const [linkSlug, setLinkSlug] = useState<string | null>(null);

  const filteredDeals = useMemo(
    () => deals?.latestDeals || [],
    [deals?.latestDeals]
  );

  const customBlogPosts = useMemo(
    () => customBlogPostsData || [],
    [customBlogPostsData]
  );

  const feedData = useMemo(() => {
    const dealsToUse = blogPageSEO?.dealSummary?.ASIN
      ? [blogPageSEO?.dealSummary].concat(filteredDeals)
      : filteredDeals;
    const data: FeedObjectType[] = postDataToFeedObj(dealsToUse);
    if (
      blogPageSEO?.pageDeal &&
      pageDealSlugs &&
      Array.isArray(pageDealSlugs)
    ) {
      // add page deal to pageDealSlugs
      pageDealSlugs.push(blogPageSEO?.pageDeal);
    }

    const pageDeals = pageDealsToFeedObjs(pageDealSlugs || []).filter(
      // @ts-ignore
      (deal) => deal?.dealObj?.slug !== filterOutSlug
    );

    const customBlogPostsToPass = blogPageSEO?.customBlogPost
      ? [blogPageSEO?.customBlogPost, ...customBlogPosts]
      : customBlogPosts;

    const uniqueCustomBlogPosts = uniqBy(customBlogPostsToPass, 'slug');

    const customBlogPostObjs = customBlogPostsToFeedObj(uniqueCustomBlogPosts);
    // data.push(...pageDeals, ...daysToFeed(40), bestSellingToFeedObj());
    data.push(...customBlogPostObjs, ...pageDeals, ...daysToFeed(40));

    return data;
  }, [
    filteredDeals,
    pageDealSlugs,
    filterOutSlug,
    blogPageSEO?.dealSummary,
    customBlogPosts
  ]);

  const sortedFeedData = useMemo(() => {
    const sortedData = orderBy(feedData, ['postDate'], ['desc']).filter(
      (stuff) => stuff !== null
    );
    const blogSEOSlug =
      blogPageSEO?.dealSummary?.slug || blogPageSEO?.customBlogPost?.slug;
    if (linkSlug || blogSEOSlug) {
      const linkedFeedObj = find(
        sortedData,
        ({ permalink }) => permalink === (linkSlug || blogSEOSlug)
      );

      if (linkedFeedObj) {
        remove(
          sortedData,
          ({ permalink }) => permalink === (linkSlug || blogSEOSlug)
        );
        linkedFeedObj.isMainContent = true;
        sortedData.unshift(linkedFeedObj);
      }
    }

    if (!user && !hasSignedUpForMailingLists) {
      // insert email sign up prompt into position emailSignUpPromptPosition
      const emailSignUpPrompt = {
        dealObjType: 'email',
        postDate: new Date().toISOString(),
        permalink: 'email-sign-up-prompt',
        title: 'Sign up for our email list!',
        dealObj:
          'Sign up for our email list to get the best deals delivered to your inbox.'
      };

      sortedData.splice(emailSignUpPromptPosition, 0, emailSignUpPrompt);
    }
    return sortedData;
  }, [feedData, linkSlug, user, blogPageSEO]);

  const limitedFeed = useMemo(() => {
    const expandedLimit =
      sortedFeedData?.[0]?.dealObjType === 'day-bar' ? limit + 1 : limit;
    return limit ? slice(sortedFeedData, 0, expandedLimit) : sortedFeedData;
  }, [sortedFeedData, limit]);

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 250);
  }, []);

  const loadMore = useCallback(
    debounce(() => {
      trackUse({
        item: 'more-blog-posts',
        value: `${limit + loadingLimit}`,
        type: 'SCROLL'
      });
      logPostHogEvent('infinite-load-more-posts-on-blog', {
        scrollDepthOfPostsSoFar: `${limit + loadingLimit}`,
        type: 'SCROLL'
      });
      setLimit((prev) => prev + loadingLimit);
    }, 300),
    [limit]
  );

  const renderFeedObject = (feedObj: FeedObjectType, index: number) => {
    switch (feedObj?.dealObjType) {
      case 'ASIN-deal':
        return (
          <DealPostFeedCard
            postDate={feedObj?.postDate}
            deal={feedObj.dealObj as DealPostType}
            permalink={feedObj?.permalink}
            isMainContent={!!feedObj?.isMainContent}
            isFirstContent={index === 0}
            relatedDeals={feedObj?.relatedDeals}
          />
        );
      case 'best-selling-week':
        return <TopDealsList postDate={feedObj?.postDate} />;
      case 'email':
        return (
          <Box m={0}>
            <Title
              link="/email"
              title="Sign up for our email list to get the best deals delivered to your inbox"
              backgroundColor="#0e4686"
            />
            <Box mt={4} pb={4}>
              <Email
                showPageContainer={false}
                showReasons={false}
                signUpLocation="blog"
              />
            </Box>
          </Box>
        );
      case 'day-bar':
        return (
          <Box
            sx={{
              // borderBottom: '1px solid #ddd',
              padding: '1.2rem 12px 1rem 12px',
              backgroundColor: '#7a3159',
              marginLeft: '-17px !important',
              marginRight: '-16px !important'
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                '@media (max-width: 540px)': {
                  fontSize: '18px'
                }
              }}
            >
              {format(
                new Date(feedObj?.dealObj as string),
                "EEEE do 'of' MMMM"
              )}
            </Typography>
          </Box>
        );
      case 'page-deal':
        return (
          <PageDealFeedCard
            pageDeal={feedObj?.dealObj as PageDeal}
            permalink={feedObj?.permalink}
            isMainContent={!!feedObj?.isMainContent}
          />
        );
      case 'custom-blog-post':
        return (
          <CustomBlogPostCard
            customBlogPost={feedObj?.dealObj as CustomBlogPostUser}
            permalink={feedObj?.permalink}
            isMainContent={!!feedObj?.isMainContent}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (renderInContainer) {
      const urlSlug = location.pathname.split('/').pop();
      if (urlSlug && !['feed', 'blog'].includes(urlSlug)) {
        setLimit(1);
        setLinkSlug(urlSlug);
        scrollToTop();
      }
      return () => {
        dispatch({ type: DEALS_UPDATE_BLOG_PAGE_SEO, blogPageSEO: null });
      };
    }
    return null;
  }, [location.pathname, renderInContainer, dispatch, scrollToTop]);

  useEffect(() => {
    if (blogPageSEO?.dealSummary?.slug) {
      setLinkSlug(blogPageSEO?.dealSummary?.slug);
      scrollToTop();
    }
  }, [blogPageSEO?.dealSummary?.slug, scrollToTop]);

  useEffect(() => {
    if (blogPageSEO?.pageDeal?.slug) {
      setLinkSlug(`page-deal-${blogPageSEO?.pageDeal?.slug}`);
      scrollToTop();
    }
  }, [blogPageSEO?.pageDeal?.slug, scrollToTop]);

  const shouldRender = useMemo(() => {
    if (blogPageSEO?.dealSummary?.ASIN) {
      return true;
    }

    return (
      deals &&
      pageDealSlugs &&
      !isLoadingDeals &&
      !isLoadingPageDealSlugs &&
      !isLoadingCustomBlogPosts
    );
  }, [
    deals,
    pageDealSlugs,
    isLoadingDeals,
    isLoadingPageDealSlugs,
    blogPageSEO
  ]);

  return (
    <BlogContainer
      renderInContainer={renderInContainer}
      shouldRenderSEO={shouldRenderSEO}
      linkSlug={linkSlug}
    >
      <span id="feed" />
      {dealSummaryError && renderInContainer && (
        <Box textAlign="center" data-testid="error-div" mb={8} mt={8}>
          <Typography variant="h4">404</Typography>
          <Typography variant="h6">Page not found</Typography>

          <Typography
            variant="subtitle1"
            sx={{
              marginTop: 2
            }}
          >
            We can&apos;t find the deal you&apos;re looking for in our list but
            do check out our latest deals below.
          </Typography>
        </Box>
      )}
      <Box
        data-testid="background-div"
        sx={{
          position: 'relative',
          '@media (max-width: 768px)': {
            marginLeft: '0px',
            marginRight: '0px'
          }
        }}
      >
        {shouldRender ? (
          <InfiniteScroll
            dataLength={limitedFeed.length}
            next={loadMore}
            hasMore
            loader={
              <>
                <SkeletonBlogCard key="0" />
                <SkeletonBlogCard key="1" />
              </>
            }
            style={{ overflow: 'visible' }}
            scrollThreshold={limit === 1 && !isMobile ? 0.4 : 0.7}
          >
            {map(limitedFeed || [], (deal, i) => (
              <Box
                key={deal?.permalink || i}
                sx={{
                  display: 'flex',
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    display: 'table',
                    clear: 'both'
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    '@media (max-width: 540px)': {
                      width: '100%'
                    }
                  }}
                >
                  {renderFeedObject(deal, i)}
                </Box>
              </Box>
            ))}
          </InfiniteScroll>
        ) : (
          <SkeletonBlogCard />
        )}
      </Box>
      <ScrollTop />
    </BlogContainer>
  );
};

const mapStateToProps = ({
  home: { blogPageSEO, dealSummaryError }
}: AppState) => ({
  blogPageSEO,
  dealSummaryError
});

export default connect(mapStateToProps)(Blog);
